import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { toCamelCase } from "../../utils";
import { getAllFDs } from "../../redux/fd/fdSlice";
import AppLoader from "../../components/AppLoader/AppLoader";
import {
  bodyCellSx,
  BORDER,
  DETAIL_CARD_BG,
  EmptyTableRow,
  ExpandButton,
  headCellSx,
  ICON_BLUE,
  ListingAvatar,
  ListingPageShell,
  schemeHeadCellSx,
} from "../../components/AssetListing/assetListingShared";
import { DARK_NAVY, DARK_NAVY_LIGHT, PROFILE_FONT } from "../../constants/layout";

const FDTableRow = ({ data }: { data: any }) => {
  const [open, setOpen] = useState(false);
  const schemes = data?.schemes || [];

  return (
    <>
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{ cursor: "pointer", bgcolor: "#FCFCFC", "&:hover": { bgcolor: "#F5F5F5" } }}
      >
        <TableCell sx={bodyCellSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <ListingAvatar name={data.bankName} />
            <Box>
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 14, fontWeight: 600, color: DARK_NAVY }}>
                {toCamelCase(data.bankName)}
              </Typography>
              <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 12, color: DARK_NAVY_LIGHT, mt: 0.25 }}>
                {schemes.length} scheme{schemes.length !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell sx={bodyCellSx} align="center">
          <ExpandButton open={open} onClick={() => setOpen(!open)} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={2} sx={{ py: 0, borderBottom: open ? `1px solid #F5F5F5` : "none" }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ px: { xs: 1, md: 2 }, py: 1.5 }}>
              <TableContainer
                sx={{
                  borderRadius: "12px",
                  border: `1px solid ${BORDER}`,
                  bgcolor: DETAIL_CARD_BG,
                  overflow: "hidden",
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={schemeHeadCellSx}>Scheme Name</TableCell>
                      <TableCell sx={schemeHeadCellSx} align="center">
                        Credit Rating
                      </TableCell>
                      <TableCell sx={schemeHeadCellSx} align="center">
                        Max Interest Rate
                      </TableCell>
                      <TableCell sx={schemeHeadCellSx} align="center">
                        Min Investment
                      </TableCell>
                      <TableCell sx={schemeHeadCellSx} align="center">
                        Max Investment
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {schemes.map((scheme: any) => (
                      <TableRow key={scheme.id} hover sx={{ "&:hover": { bgcolor: "#FAFAFA" } }}>
                        <TableCell sx={{ ...bodyCellSx, py: 1.25 }}>
                          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, fontWeight: 600, color: DARK_NAVY }}>
                            {scheme.schemeName}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, py: 1.25, color: DARK_NAVY_LIGHT }} align="center">
                          {scheme.creditRating || "-"}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, py: 1.25 }} align="center">
                          <Typography sx={{ fontFamily: PROFILE_FONT, fontSize: 13, fontWeight: 700, color: ICON_BLUE }}>
                            {scheme.maxInterestRate
                              ? `${Number(scheme.maxInterestRate).toFixed(2)}%`
                              : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, py: 1.25, color: DARK_NAVY_LIGHT }} align="center">
                          {scheme.minInvestmentAmount ?? "-"}
                        </TableCell>
                        <TableCell sx={{ ...bodyCellSx, py: 1.25, color: DARK_NAVY_LIGHT }} align="center">
                          {scheme.maxInvestmentamount ?? "-"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const FDListing = () => {
  const dispatch = useDispatch<any>();
  const [loader, setLoader] = useState(true);
  const [searchFund, setSearchFund] = useState<string>("");
  const [fdData, setFDData] = useState<any[]>([]);
  const [allFdData, setAllFdData] = useState<any[]>([]);

  const fetchData = async () => {
    setLoader(true);
    try {
      const response = await dispatch(
        getAllFDs({
          pageIndex: 1,
          pageSize: 10000,
          minInvest: 0,
          minTenure: "",
          minInterest: 0,
          maxInterest: 0,
          search: "",
        })
      );
      const data = response?.payload || [];
      setAllFdData(data);
      setFDData(data);
    } catch (error) {
      setFDData([]);
      setAllFdData([]);
      console.error("API Error:", error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!searchFund) {
      setFDData(allFdData);
      return;
    }
    const q = searchFund.toLowerCase();
    setFDData(allFdData.filter((plan: any) => plan.bankName?.toLowerCase().includes(q)));
  }, [searchFund, allFdData]);

  return (
    <>
      <AppLoader loading={loader} />
      <ListingPageShell
        title="Corporate FD"
        subtitle="Compare corporate fixed deposit providers, rates and investment limits."
        searchValue={searchFund}
        onSearchChange={setSearchFund}
        searchPlaceholder="Search fixed deposit by provider name..."
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Provider</TableCell>
                <TableCell sx={{ ...headCellSx, width: 72 }} align="center">
                  Details
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!fdData?.length ? (
                <TableRow>
                  <EmptyTableRow colSpan={2} />
                </TableRow>
              ) : (
                fdData.map((row, index) => <FDTableRow key={row.bankName ?? index} data={row} />)
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </ListingPageShell>
    </>
  );
};

export default FDListing;
